import type { SocialPostFormData, SocialPostResult } from '@/pages/social-post/types';
import { getSupabaseFunctionUrl } from '@/lib/supabase';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';
import type { DailyContentResult, DailyPostItem } from '@/pages/social-post/types';

export class SocialPostInsufficientCreditsError extends Error {
  creditsRemaining: number;
  constructor(remaining: number) {
    super('Insufficient credits');
    this.creditsRemaining = remaining;
  }
}

export interface DailyContentPayload {
  mode: 'daily';
  quantity: string;
  contentPersonality: string;
  audienceFocus: string;
  location: string;
  dailyGoal: string;
  contentThemes: string[];
  avoidRepetition: boolean;
}

export interface BusinessContext {
  businessDescription?: string;
  servicesOffered?: string;
  businessType?: string;
  location?: string;
  uniqueDifferentiator?: string;
  defaultAudience?: string;
  defaultTone?: string;
  brandPositioning?: string;
}

export interface SocialPostGenerateOptions {
  signal?: AbortSignal;
}

function createAbortError() {
  return new DOMException('Generation stopped.', 'AbortError');
}

function toInsufficientCreditsError(error: unknown): SocialPostInsufficientCreditsError | null {
  if (!error || typeof error !== 'object') return null;

  const status = 'status' in error ? (error as { status?: number }).status : undefined;
  const body = 'body' in error ? (error as { body?: Record<string, unknown> }).body : undefined;

  if (status !== 403) return null;

  return new SocialPostInsufficientCreditsError(
    typeof body?.creditsRemaining === 'number' ? body.creditsRemaining : 0
  );
}

function normalizeDailyPosts(posts: unknown): DailyPostItem[] {
  if (!Array.isArray(posts)) return [];

  return posts.map((post, idx) => {
    const item = (post ?? {}) as Partial<DailyPostItem>;
    return {
      day: typeof item.day === 'number' ? item.day : idx + 1,
      postType: typeof item.postType === 'string' ? item.postType : 'Daily Post',
      contentCategory: typeof item.contentCategory === 'string' ? item.contentCategory : 'Engagement',
      hook: typeof item.hook === 'string' ? item.hook : '',
      caption: typeof item.caption === 'string' ? item.caption : '',
      cta: typeof item.cta === 'string' ? item.cta : '',
      hashtags: Array.isArray(item.hashtags) ? item.hashtags.filter((tag): tag is string => typeof tag === 'string') : [],
      imageDirection: typeof item.imageDirection === 'string' ? item.imageDirection : '',
      shortVideoIdea: typeof item.shortVideoIdea === 'string' ? item.shortVideoIdea : '',
    };
  });
}

async function callGenerateSocialPost<T = SocialPostResult>(
  payload: Record<string, unknown>,
  options: SocialPostGenerateOptions = {}
): Promise<{ data: T; status: number }> {
  if (options.signal?.aborted) throw createAbortError();
  const headers = await getEdgeFunctionHeaders();
  if (options.signal?.aborted) throw createAbortError();

  const response = await fetch(getSupabaseFunctionUrl('generate-social-post'), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: options.signal,
  });

  let body: Record<string, unknown> = {};
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok) {
    throw Object.assign(
      new Error(typeof body.error === 'string' ? body.error : `Request failed: ${response.status}`),
      { status: response.status, body }
    );
  }

  return {
    data: body as T,
    status: response.status,
  };
}

export async function generateSocialPost(
  formData: SocialPostFormData,
  userId?: string,
  logoUrl?: string,
  dailyPayload?: DailyContentPayload,
  businessContext?: BusinessContext,
  options: SocialPostGenerateOptions = {}
): Promise<SocialPostResult> {
  const basePayload = {
    identityType: formData.identityType,
    businessName: formData.businessName,
    creatorNiche: formData.creatorNiche || undefined,
    creatorPersonality: formData.creatorPersonality || undefined,
    contentPillars: formData.contentPillars || undefined,
    creatorAudienceType: formData.creatorAudienceType || undefined,
    creatorTone: formData.creatorTone || undefined,
    contentStyle: formData.contentStyle || undefined,
    businessDescription: businessContext?.businessDescription || undefined,
    servicesOffered: businessContext?.servicesOffered || undefined,
    businessType: businessContext?.businessType || undefined,
    location: businessContext?.location || undefined,
    uniqueDifferentiator: businessContext?.uniqueDifferentiator || undefined,
    defaultAudience: businessContext?.defaultAudience || undefined,
    defaultTone: businessContext?.defaultTone || undefined,
    brandPositioning: businessContext?.brandPositioning || undefined,
    platform: formData.platform,
    postType: formData.postType,
    goal: formData.goal,
    offerTopic: formData.offerTopic,
    targetAudience: formData.targetAudience,
    brandVoice: formData.brandVoice,
    creativeDirection: formData.creativeDirection,
    captionLength: formData.captionLength,
    includeImage: formData.includeImage,
    cta: formData.cta || undefined,
    hashtags: formData.hashtags || undefined,
    specialInstructions: formData.specialInstructions || undefined,
    theme: formData.theme || undefined,
    eventPromotion: formData.eventPromotion || undefined,
    referenceStyle: formData.referenceStyle || undefined,
    imageStyle: formData.imageStyle || undefined,
    autoMode: formData.autoMode || undefined,
    referenceImageBase64: formData.referenceImageBase64 || undefined,
    referenceImagePrompt: formData.referenceImagePrompt || undefined,
    logoUrl: logoUrl || undefined,
    ...(dailyPayload ?? {}),
  };

  let data: SocialPostResult;
  try {
    ({ data } = await callGenerateSocialPost<SocialPostResult>(
      formData.includeImage && !dailyPayload
        ? { ...basePayload, includeImage: false }
        : basePayload,
      options
    ));
  } catch (error) {
    const creditsError = toInsufficientCreditsError(error);
    if (creditsError) throw creditsError;
    throw error;
  }

  if (!formData.includeImage || dailyPayload) {
    if (dailyPayload) {
      const dailyData = data as unknown as Partial<DailyContentResult> & { posts?: unknown };
      return {
        type: 'daily_content',
        quantity: dailyPayload.quantity as DailyContentResult['quantity'],
        platform: typeof dailyData.platform === 'string' ? dailyData.platform : formData.platform,
        businessName: typeof dailyData.businessName === 'string' ? dailyData.businessName : formData.businessName,
        posts: normalizeDailyPosts(dailyData.posts),
      } as unknown as SocialPostResult;
    }

    return { ...data, type: 'social_post' };
  }

  if (!data.imageConcept) {
    return { ...data, type: 'social_post' };
  }

  if (options.signal?.aborted) throw createAbortError();

  let imageData: Partial<SocialPostResult> & { imageUrl?: string | null; creditsRemaining?: number };
  try {
    ({ data: imageData } = await callGenerateSocialPost<Partial<SocialPostResult> & { imageUrl?: string | null; creditsRemaining?: number }>({
      ...basePayload,
      imageOnly: true,
      imageConceptOverride: data.imageConcept,
    }, options));
  } catch (error) {
    const creditsError = toInsufficientCreditsError(error);
    if (creditsError) throw creditsError;
    throw error;
  }
  return {
    ...data,
    imageUrl: typeof imageData.imageUrl === 'string' ? imageData.imageUrl : null,
    type: 'social_post',
  };
}
